// Lets a background mechanism postpone navigating away while something on the page cannot
// survive it.
//
// A live Amazon Connect chat exists only in this tab's memory — the session handle, the
// transcript and the participant token are never persisted, and nothing reconnects — so a
// reload or a redirect ends the conversation outright and leaves the agent on the other end
// talking to nobody. Two mechanisms navigate on their own schedule rather than the user's, and
// they want different things from a hold: the re-auth redirect has to happen — the app is
// unusable without a token — so it queues (navigateWhenReleased), while the stale-build
// reloader has any number of later opportunities and simply skips this one (isNavigationHeld).
//
// This covers only navigation the app itself initiates. A user reloading or closing the tab
// still ends the chat, which is theirs to decide.

// Counted, not boolean, so concurrent holders cannot release each other's hold.
let holds = 0;

// Every deferred action here navigates, which tears this document down — so a second one
// could never run. One slot, last writer wins. Both orderings converge: a re-auth redirect
// that replaces a pending reload comes back through a fresh index.html anyway, and a reload
// that replaces a pending re-auth is followed by the next v2 call asking for it again.
let pendingNavigation: (() => void) | null = null;

// Returns the release function. Safe to call more than once — React runs effect cleanups
// twice under StrictMode, and a double release would drop someone else's hold.
export function holdNavigation(): () => void {
  holds += 1;
  let released = false;

  return () => {
    if (released) return;
    released = true;
    holds -= 1;
    if (holds > 0) return;

    const navigate = pendingNavigation;
    pendingNavigation = null;
    navigate?.();
  };
}

export function isNavigationHeld(): boolean {
  return holds > 0;
}

// Navigate now, or as soon as nothing is holding it back.
export function navigateWhenReleased(navigate: () => void): void {
  if (!isNavigationHeld()) {
    navigate();
    return;
  }
  pendingNavigation = navigate;
}
